import { computed, IComputedValue, IReactionDisposer, reaction } from 'mobx';

/**
 * One mobx computed per key, each kept current by a reaction until cleared, so a value keeps its identity until `equals`
 * says it changed. Entries live until {@link clear}; callers clear it when the user or their permissions change.
 */
export class KeyedComputedCache<T> {
    private readonly entries = new Map<string, { value: IComputedValue<T>, dispose: IReactionDisposer }>();

    constructor(private readonly equals: (a: T, b: T) => boolean) {}

    /** The value for `key`, computed by `compute` on first use and recomputed when the observables it reads change. */
    get(key: string, compute: () => T): T {
        let entry = this.entries.get(key);
        if (!entry) {
            const value = computed(compute, { equals: this.equals });
            entry = { value, dispose: reaction(() => value.get(), () => undefined) };
            this.entries.set(key, entry);
        }
        return entry.value.get();
    }

    /** Disposes and drops every entry. */
    clear(): void {
        this.entries.forEach(entry => entry.dispose());
        this.entries.clear();
    }
}
